import { type ReactNode, useId } from "react";
import { isMoneyValid } from "../core/primitives/scan-edit";
import { CountStepper } from "./CountStepper";
import { Switch, TextField } from "./EditorPieces";

/** The count beside the price: copies to confirm, or the copies on hand. */
export interface PanelCount {
  readonly value: number;
  readonly onChange: (count: number) => void;
  readonly label: string;
  readonly minimum?: number;
  /** Zero reads as this ("Untracked"), which confirms without a count. */
  readonly zeroLabel?: string;
}

/**
 * The price, the floor and a count, set the same way wherever the merchant meets
 * a card: the review screen and the card page share it, as iOS's do. Off, the
 * floor follows the asking price, one figure for both. A port of the iOS
 * `AdminPricePanel`.
 */
export function PricePanel({
  price,
  onPrice,
  count,
  separateFloor,
  onSeparateFloor,
  floor,
  onFloor,
  floorError,
  priceError = null,
  disabled = false,
  children,
}: {
  price: string;
  onPrice: (price: string) => void;
  /** None: a customer's scan is priced, never stocked. */
  count?: PanelCount;
  separateFloor: boolean;
  onSeparateFloor: (on: boolean) => void;
  floor: string;
  onFloor: (floor: string) => void;
  floorError: string | null;
  /** Said under the price; where a page says it elsewhere (the review screen's bar), none. */
  priceError?: string | null;
  /** Holds the panel still: the fields keep their cursor, the buttons stop. */
  disabled?: boolean;
  /** The notes under the panel. */
  children?: ReactNode;
}) {
  const priceId = useId();
  const priceErrorId = useId();
  const valid = isMoneyValid(price);
  return (
    <div className="space-y-3 rounded-row border border-line bg-surface p-3.5">
      <div className="flex items-end gap-3">
        <div className="min-w-0 flex-1">
          <label htmlFor={priceId} className="mb-1.5 block text-xs font-medium text-muted">
            {separateFloor ? "Asking price" : "Asking and floor price"}
          </label>
          <div
            className={`flex h-[46px] items-center gap-1 rounded-btn border bg-app/40 px-3 text-[22px] font-bold tabular-nums text-accent ${
              valid ? "border-line focus-within:border-accent" : "border-warn"
            }`}
          >
            <span className="text-accent/70">$</span>
            <input
              id={priceId}
              inputMode="decimal"
              placeholder="0.00"
              className="w-full min-w-0 bg-transparent outline-none placeholder:font-normal placeholder:text-faint"
              value={price}
              readOnly={disabled}
              aria-invalid={valid ? undefined : true}
              aria-describedby={priceError ? priceErrorId : undefined}
              onChange={(e) => onPrice(e.target.value)}
            />
          </div>
        </div>
        {count && (
          <div className="flex shrink-0 flex-col items-end">
            <span className="mb-1.5 text-xs font-medium text-muted">{count.label}</span>
            <CountStepper
              count={count.value}
              onChange={count.onChange}
              minimum={count.minimum}
              zeroLabel={count.zeroLabel}
              disabled={disabled}
              tall
            />
          </div>
        )}
      </div>
      {/* Under the row, so the count beside the price keeps its line. */}
      {priceError && (
        <p id={priceErrorId} className="field-error -mt-1.5 text-xs">
          {priceError}
        </p>
      )}

      <Switch label="Separate floor price" checked={separateFloor} onChange={onSeparateFloor} disabled={disabled} />
      {separateFloor && (
        <TextField
          label="Floor price"
          value={floor}
          onChange={onFloor}
          placeholder="Optional"
          inputMode="decimal"
          error={floorError}
          readOnly={disabled}
        />
      )}

      {children}
    </div>
  );
}
